import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import CONFIG from '../../config';
import './DoctorDashboard.css';
import Profile from '../AdminDashboard/Profile';
import DoctorDevTools from './DoctorDevTools';
import LoadingSpinner from '../common/LoadingSpinner';
import { SimulationProvider } from '../../contexts/SimulationContext';

const DoctorDashboard = ({ onLogout }) => {
    const [assignedProjects, setAssignedProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [activeTab, setActiveTab] = useState('evaluator'); // evaluator, results, profile
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Medical Evaluator State
    const [medicalQueue, setMedicalQueue] = useState([]);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [candidateHistory, setCandidateHistory] = useState(null);
    const [existingRecord, setExistingRecord] = useState(null);

    // Results State
    const [processedCandidates, setProcessedCandidates] = useState([]);
    const [viewingResult, setViewingResult] = useState(null);

    // Form State
    const [vitals, setVitals] = useState({
        bp: '',
        heart_rate: '',
        temperature: '',
        spo2: '',
        weight_kg: '',
        height_cm: ''
    });
    const [remarks, setRemarks] = useState('');
    const [result, setResult] = useState('PASS');
    const [loading, setLoading] = useState(false);
    const [isCorrection, setIsCorrection] = useState(false);
    const [useStoredSignature, setUseStoredSignature] = useState(false);

    // Filtering State
    const [filters, setFilters] = useState({
        name: '',
        chestNumber: '',
        uniqueId: '',
        result: 'ALL'
    });

    // Signature Canvas Ref
    const canvasRef = useRef(null);
    const [isSigned, setIsSigned] = useState(false);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const [dbUser, setDbUser] = useState(null);

    useEffect(() => {
        fetchAssignedProjects();
        fetchDbUser();
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchQueue();
            if (activeTab === 'results') {
                fetchProcessed();
            }
            if (activeTab === 'evaluator') {
                fetchDbUser();
            }
        }
    }, [selectedProject, activeTab]);

    const fetchDbUser = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/auth/profile/${user.id || user._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDbUser(resp.data);
            if (resp.data.signature_hash) {
                setUseStoredSignature(true);
            }
        } catch (err) {
            console.error('Failed to fetch user profile:', err);
        }
    };

    const fetchAssignedProjects = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/projects/assigned`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAssignedProjects(resp.data);
            if (resp.data.length > 0) {
                setSelectedProject(resp.data[0]);
            }
        } catch (err) {
            console.error('Failed to fetch projects:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchQueue = async () => {
        if (!selectedProject) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/medical-queue/${selectedProject._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMedicalQueue(resp.data);
        } catch (err) {
            console.error('Failed to fetch medical queue:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProcessed = async () => {
        if (!selectedProject) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/medical/processed/${selectedProject._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProcessedCandidates(resp.data);
        } catch (err) {
            console.error('Failed to fetch processed candidates:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadCandidate = async (cand) => {
        setSelectedCandidate(cand);
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            setCandidateHistory(cand.test_results);

            const recResp = await axios.get(`${CONFIG.API_URL}/api/medical/record/${cand._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (recResp.data) {
                setExistingRecord(recResp.data);
                setVitals(recResp.data.vitals);
                setRemarks(recResp.data.remarks);
                setResult(recResp.data.result);
            } else {
                setExistingRecord(null);
                setVitals({ bp: '', heart_rate: '', temperature: '', spo2: '', weight_kg: '', height_cm: '' });
                setRemarks('');
                setResult('PASS');
                clearSignature();
            }
            setIsCorrection(false);
        } catch (err) {
            console.error('Failed to load candidate details:', err);
        } finally {
            setLoading(false);
        }
    };

    // Signature Logic
    useEffect(() => {
        if (selectedCandidate && canvasRef.current && (!existingRecord || isCorrection) && !useStoredSignature) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.strokeStyle = '#000';

            let drawing = false;

            const startDrawing = (e) => {
                drawing = true;
                draw(e);
            };
            const stopDrawing = () => {
                drawing = false;
                ctx.beginPath();
            };
            const draw = (e) => {
                if (!drawing) return;
                const rect = canvas.getBoundingClientRect();
                const x = (e.clientX || e.touches[0].clientX) - rect.left;
                const y = (e.clientY || e.touches[0].clientY) - rect.top;

                ctx.lineTo(x, y);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(x, y);
                setIsSigned(true);
            };

            canvas.addEventListener('mousedown', startDrawing);
            canvas.addEventListener('mouseup', stopDrawing);
            canvas.addEventListener('mousemove', draw);
            canvas.addEventListener('touchstart', startDrawing);
            canvas.addEventListener('touchend', stopDrawing);
            canvas.addEventListener('touchmove', draw);

            return () => {
                canvas.removeEventListener('mousedown', startDrawing);
                canvas.removeEventListener('mouseup', stopDrawing);
                canvas.removeEventListener('mousemove', draw);
                canvas.removeEventListener('touchstart', startDrawing);
                canvas.removeEventListener('touchend', stopDrawing);
                canvas.removeEventListener('touchmove', draw);
            };
        }
    }, [selectedCandidate, existingRecord, isCorrection, useStoredSignature]);

    const clearSignature = () => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setIsSigned(false);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        let signatureData = '';
        if (useStoredSignature) {
            if (!dbUser?.signature_hash) {
                Swal.fire('No Stored Signature', 'Please upload your signature in the Profile tab first.', 'error');
                return;
            }
            signatureData = dbUser.signature_hash;
        } else {
            if (!isSigned && !existingRecord) {
                Swal.fire('Signature Required', 'Please provide a digital signature or use your stored signature.', 'error');
                return;
            }
            signatureData = canvasRef.current.toDataURL();
        }

        const confirmText = result === 'PASS'
            ? `Are you sure you want to mark ${selectedCandidate.full_name} as PASSED? This will move them to COMPLETED status.`
            : `Are you sure you want to mark ${selectedCandidate.full_name} as FAILED? This will remove them from the pipeline.`;

        const confirmation = await Swal.fire({
            title: 'Confirm Medical Evaluation',
            text: confirmText,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: result === 'PASS' ? '#10b981' : '#ef4444',
            confirmButtonText: result === 'PASS' ? 'Yes, Pass Candidate' : 'Yes, Fail Candidate'
        });

        if (!confirmation.isConfirmed) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');

            const endpoint = isCorrection ? '/api/medical/correction' : '/api/medical/submit';
            const payload = {
                candidateId: selectedCandidate._id,
                projectId: selectedProject._id,
                vitals,
                remarks,
                result,
                signature_hash: signatureData,
                originalRecordId: isCorrection ? existingRecord._id : null
            };

            await axios.post(`${CONFIG.API_URL}${endpoint}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                icon: 'success',
                title: isCorrection ? 'Correction Saved' : 'Medical Record Saved',
                text: `Candidate ${selectedCandidate.full_name} processed successfully as ${result}.`,
                timer: 2000,
                showConfirmButton: false
            });

            setSelectedCandidate(null);
            fetchQueue();
            fetchProcessed();
        } catch (err) {
            Swal.fire('Submission Error', err.response?.data?.message || 'Failed to save record.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const menuGroups = [
        {
            title: 'MEDICAL CENTER',
            items: [
                { id: 'evaluator', icon: 'ion:medkit-outline', label: 'Medical Evaluation' },
                { id: 'results', icon: 'ion:ribbon-outline', label: 'Processed Results' },
            ]
        },
        {
            title: 'USER SETTINGS',
            items: [
                { id: 'profile', icon: 'ion:person-outline', label: 'My Profile' }
            ]
        }
    ];

    const renderEvaluator = () => (
        <div className="dr-dashboard-view">
            <div className="dr-eval-grid">
                {/* Left: Queue Control */}
                <div className="dr-glass-card dr-queue-pane">
                    <div className="dr-pane-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon icon="ion:people-outline" width="24" color="var(--dr-primary)" />
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 900 }}>MEDICAL QUEUE</h3>
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--dr-primary)', background: 'rgba(128,0,0,0.05)', padding: '4px 10px', borderRadius: '20px' }}>
                            {medicalQueue.length} Waiting
                        </span>
                    </div>
                    <div className="dr-queue-list hide-scrollbar">
                        {loading && medicalQueue.length === 0 ? (
                            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                                <LoadingSpinner compact message="Syncing Queue..." />
                            </div>
                        ) : medicalQueue.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 20px', opacity: 0.5 }}>
                                <Icon icon="ion:cafe-outline" width="48" style={{ marginBottom: '12px', color: 'var(--dr-text-muted)' }} />
                                <div style={{ fontWeight: 800, color: '#94a3b8', fontSize: '13px' }}>QUEUE IS EMPTY</div>
                                <p style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '4px' }}>Candidates will appear here after physical tests.</p>
                            </div>
                        ) : (
                            medicalQueue.map(cand => (
                                <div
                                    key={cand._id}
                                    className={`dr-queue-item ${selectedCandidate?._id === cand._id ? 'active' : ''}`}
                                    onClick={() => loadCandidate(cand)}
                                >
                                    <div style={{ width: '40px', height: '40px', background: selectedCandidate?._id === cand._id ? 'var(--dr-primary)' : '#f1f5f9', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: selectedCandidate?._id === cand._id ? 'white' : 'var(--dr-primary)', marginRight: '16px' }}>
                                        {cand.chest_number}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dr-text-main)' }}>{cand.full_name}</div>
                                        <div style={{ fontSize: '11px', color: 'var(--dr-text-muted)' }}>ID: {cand.unique_id}</div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Right: Evaluation Pane */}
                <div className="dr-glass-card dr-eval-pane" style={{ background: 'white' }}>
                    {!selectedCandidate ? (
                        <div className="dr-eval-empty">
                            <div className="dr-eval-empty-header">
                                <Icon icon="solar:user-rounded-bold-duotone" width="32" color="white" />
                                <span>STATION READY</span>
                            </div>
                            <div style={{ padding: '60px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <div className="dr-empty-icon-pulse">
                                    <Icon icon="solar:users-group-rounded-bold-duotone" width="64" style={{ color: 'var(--dr-primary)', opacity: 0.15 }} />
                                </div>
                                <h3 style={{ fontSize: '24px', fontWeight: 900, color: 'var(--dr-text-main)', marginTop: '24px', marginBottom: '12px' }}>AWAITING CANDIDATE</h3>
                                <p style={{ fontSize: '14px', color: 'var(--dr-text-muted)', maxWidth: '300px', lineHeight: 1.6, fontWeight: 500 }}>
                                    Please select a candidate from the left queue to begin the medical evaluation process.
                                </p>
                            </div>
                        </div>
                    ) : loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <LoadingSpinner message="Loading Candidate Record..." />
                        </div>
                    ) : (
                        <div className="dr-eval-content hide-scrollbar" style={{ padding: '40px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 900 }}>{selectedCandidate.full_name}</h2>
                                    <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                                        <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--dr-primary)' }}>CHEST: {selectedCandidate.chest_number}</span>
                                        <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--dr-text-muted)' }}>UNIQUE ID: {selectedCandidate.unique_id}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedCandidate(null)}
                                    style={{ padding: '10px 20px', borderRadius: '12px', border: 'none', background: '#f1f5f9', fontWeight: 800, fontSize: '12px', cursor: 'pointer' }}
                                >
                                    DESELECT
                                </button>
                            </div>

                            <div className="dr-history-summary">
                                <div style={{ fontSize: '12px', fontWeight: 900, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon icon="ion:time-outline" /> EVALUATION HISTORY
                                </div>
                                <div className="dr-summary-grid">
                                    {candidateHistory?.length > 0 ? candidateHistory.map((tr, idx) => (
                                        <div key={idx} className="dr-summary-item">
                                            <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 800, marginBottom: '4px' }}>{tr.stage_name}</div>
                                            <div style={{ fontSize: '13px', fontWeight: 900, color: tr.result === 'PASS' ? '#10b981' : '#ef4444' }}>{tr.result}</div>
                                        </div>
                                    )) : <div style={{ padding: '20px', fontSize: '12px', opacity: 0.5 }}>No previous test records found.</div>}
                                </div>
                            </div>

                            <form onSubmit={handleFormSubmit} className="dr-form-section">
                                <div style={{ fontSize: '14px', fontWeight: 900, color: 'var(--dr-primary)', marginBottom: '24px', borderBottom: '2px solid rgba(128,0,0,0.1)', paddingBottom: '12px' }}>
                                    MEDICAL EXAMINATION FORM
                                </div>

                                <div className="dr-fields-grid" style={{ marginBottom: '30px' }}>
                                    <div className="dr-input-group"><label>BLOOD PRESSURE (BP)</label><input disabled={!!existingRecord && !isCorrection} value={vitals.bp} onChange={(e) => setVitals({ ...vitals, bp: e.target.value })} placeholder="e.g. 120/80" /></div>
                                    <div className="dr-input-group"><label>HEART RATE (BPM)</label><input disabled={!!existingRecord && !isCorrection} type="number" value={vitals.heart_rate} onChange={(e) => setVitals({ ...vitals, heart_rate: e.target.value })} placeholder="e.g. 72" /></div>
                                    <div className="dr-input-group"><label>SPO2 (%)</label><input disabled={!!existingRecord && !isCorrection} type="number" value={vitals.spo2} onChange={(e) => setVitals({ ...vitals, spo2: e.target.value })} placeholder="e.g. 98" /></div>
                                    <div className="dr-input-group"><label>TEMPERATURE (°F)</label><input disabled={!!existingRecord && !isCorrection} type="number" step="0.1" value={vitals.temperature} onChange={(e) => setVitals({ ...vitals, temperature: e.target.value })} placeholder="e.g. 98.6" /></div>
                                    <div className="dr-input-group"><label>WEIGHT (KG)</label><input disabled={!!existingRecord && !isCorrection} type="number" step="0.1" value={vitals.weight_kg} onChange={(e) => setVitals({ ...vitals, weight_kg: e.target.value })} placeholder="e.g. 70" /></div>
                                    <div className="dr-input-group"><label>HEIGHT (CM)</label><input disabled={!!existingRecord && !isCorrection} type="number" step="0.1" value={vitals.height_cm} onChange={(e) => setVitals({ ...vitals, height_cm: e.target.value })} placeholder="e.g. 175.5" /></div>
                                </div>

                                <div className="dr-input-group" style={{ marginBottom: '30px' }}>
                                    <label>DOCTOR'S REMARKS</label>
                                    <textarea disabled={!!existingRecord && !isCorrection} rows="3" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter any medical observations..." />
                                </div>

                                <div style={{ marginBottom: '40px' }}>
                                    <label style={{ fontSize: '11px', fontWeight: 800, color: 'var(--dr-text-muted)', display: 'block', marginBottom: '12px' }}>FINAL MEDICAL DECISION</label>
                                    <div style={{ display: 'flex', gap: '20px' }}>
                                        <button type="button" disabled={!!existingRecord && !isCorrection} onClick={() => setResult('PASS')} style={{ flex: 1, padding: '16px', borderRadius: '16px', border: result === 'PASS' ? '3px solid #10b981' : '1px solid #e2e8f0', background: result === 'PASS' ? '#dcfce7' : 'white', color: result === 'PASS' ? '#166534' : 'var(--dr-text-muted)', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}><Icon icon="ion:checkmark-circle" width="24" /> PASS</button>
                                        <button type="button" disabled={!!existingRecord && !isCorrection} onClick={() => setResult('FAIL')} style={{ flex: 1, padding: '16px', borderRadius: '16px', border: result === 'FAIL' ? '3px solid #ef4444' : '1px solid #e2e8f0', background: result === 'FAIL' ? '#fee2e2' : 'white', color: result === 'FAIL' ? '#991b1b' : 'var(--dr-text-muted)', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}><Icon icon="ion:close-circle" width="24" /> FAIL</button>
                                    </div>
                                </div>

                                <div className="dr-signature-pad-wrapper">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <label style={{ fontSize: '11px', fontWeight: 800, color: 'var(--dr-text-muted)', display: 'block', margin: 0 }}>DIGITAL AUTHORIZATION</label>
                                        {dbUser?.signature_hash && (
                                            <button
                                                type="button"
                                                onClick={() => setUseStoredSignature(!useStoredSignature)}
                                                style={{
                                                    border: '1px solid var(--dr-primary)',
                                                    background: useStoredSignature ? 'var(--dr-primary)' : 'white',
                                                    color: useStoredSignature ? 'white' : 'var(--dr-primary)',
                                                    fontSize: '10px',
                                                    fontWeight: 900,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '6px 12px',
                                                    borderRadius: '20px',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: useStoredSignature ? '0 4px 12px rgba(128,0,0,0.2)' : 'none'
                                                }}
                                            >
                                                <Icon icon={useStoredSignature ? "ion:checkmark-done" : "ion:cloud-done-outline"} width="14" />
                                                {useStoredSignature ? "USING STORED SIGNATURE" : "USE MY STORED SIGNATURE"}
                                            </button>
                                        )}
                                    </div>

                                    {existingRecord && !isCorrection ? (
                                        <div className="dr-canvas-container" style={{ padding: '20px', textAlign: 'center' }}>
                                            <img src={existingRecord.signature_hash} alt="Signature" style={{ maxHeight: '100px', opacity: 0.7 }} />
                                            <div style={{ fontSize: '11px', fontWeight: 900, color: '#166534', marginTop: '10px' }}>RECORD LOCKED BY {user.role}</div>
                                            <button type="button" onClick={() => setIsCorrection(true)} style={{ marginTop: '12px', padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--dr-primary)', color: 'var(--dr-primary)', fontWeight: 800, cursor: 'pointer', background: 'white' }}>CREATE CORRECTION</button>
                                        </div>
                                    ) : useStoredSignature ? (
                                        <div className="dr-canvas-container" style={{ padding: '20px', textAlign: 'center', background: 'rgba(128,0,0,0.02)' }}>
                                            <img src={dbUser.signature_hash} alt="Stored Signature" style={{ maxHeight: '100px', filter: 'grayscale(100%) brightness(0.5)' }} />
                                            <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--dr-primary)', marginTop: '8px' }}>AUTHORIZED VIA STORED SIGNATURE</div>
                                        </div>
                                    ) : (
                                        <div className="dr-canvas-container">
                                            <div style={{ padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '10px', fontWeight: 800, color: '#64748b' }}>DRAW ON TABLET</span>
                                                <button type="button" onClick={clearSignature} style={{ border: 'none', background: 'none', color: 'var(--dr-primary)', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>CLEAR CANVAS</button>
                                            </div>
                                            <canvas ref={canvasRef} className="dr-signature-canvas" style={{ width: '100%', height: '150px', cursor: 'crosshair' }} width="600" height="150" />
                                        </div>
                                    )}
                                </div>

                                {(!existingRecord || isCorrection) && (
                                    <button type="submit" className="dr-btn-submit" disabled={loading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                        {loading ? (
                                            <LoadingSpinner compact message="Processing..." />
                                        ) : isCorrection ? 'OVERRIDE & SAVE CORRECTION' : 'AUTHORIZE & SUBMIT MEDICAL RECORD'}
                                    </button>
                                )}
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );


    const renderResults = () => {
        const filteredCandidates = processedCandidates.filter(c => {
            const matchName = c.full_name.toLowerCase().includes(filters.name.toLowerCase());
            const matchChest = c.chest_number.toLowerCase().includes(filters.chestNumber.toLowerCase());
            const matchId = c.unique_id.toLowerCase().includes(filters.uniqueId.toLowerCase());
            const matchResult = filters.result === 'ALL' || c.result === filters.result;
            return matchName && matchChest && matchId && matchResult;
        });

        return (
            <div className="dr-dashboard-view">
                <div className="dr-glass-card" style={{ minHeight: '600px', padding: '0' }}>
                    <div style={{ padding: '30px 40px', borderBottom: '1px solid var(--dr-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h2 style={{ fontSize: '20px', fontWeight: 900, margin: 0 }}>PROCESSED CANDIDATES</h2>
                            <p style={{ fontSize: '12px', color: 'var(--dr-text-muted)', margin: '4px 0 0' }}>Review and manage candidates you have already evaluated.</p>
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
                            <LoadingSpinner message="Fetching Processed Records..." />
                        </div>
                    ) : (
                        <>
                            {/* Filter Bar */}
                            <div style={{ padding: '20px 40px', background: '#f8fafc', borderBottom: '1px solid var(--dr-border-light)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <label style={{ display: 'block', fontSize: '10px', fontWeight: 900, color: '#64748b', marginBottom: '8px' }}>SEARCH BY NAME</label>
                            <input
                                placeholder="Candidate name..."
                                value={filters.name}
                                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                                style={{ width: '100%', padding: '10px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ display: 'block', fontSize: '10px', fontWeight: 900, color: '#64748b', marginBottom: '8px' }}>CHEST NUMBER</label>
                            <input
                                placeholder="C-0001..."
                                value={filters.chestNumber}
                                onChange={(e) => setFilters({ ...filters, chestNumber: e.target.value })}
                                style={{ width: '100%', padding: '10px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ display: 'block', fontSize: '10px', fontWeight: 900, color: '#64748b', marginBottom: '8px' }}>UNIQUE ID</label>
                            <input
                                placeholder="ID..."
                                value={filters.uniqueId}
                                onChange={(e) => setFilters({ ...filters, uniqueId: e.target.value })}
                                style={{ width: '100%', padding: '10px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ display: 'block', fontSize: '10px', fontWeight: 900, color: '#64748b', marginBottom: '8px' }}>RESULT TYPE</label>
                            <select
                                value={filters.result}
                                onChange={(e) => setFilters({ ...filters, result: e.target.value })}
                                style={{ width: '100%', padding: '10px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px', background: 'white' }}
                            >
                                <option value="ALL">ALL RESULTS</option>
                                <option value="PASS">PASS ONLY</option>
                                <option value="FAIL">FAIL ONLY</option>
                            </select>
                        </div>
                    </div>

                    <div className="dr-results-table-container hide-scrollbar" style={{ padding: '20px 40px' }}>
                        <table className="dr-results-table">
                            <thead>
                                <tr>
                                    <th>CHEST NO</th>
                                    <th>FULL NAME</th>
                                    <th>EVALUATED AT</th>
                                    <th>RESULT</th>
                                    <th style={{ textAlign: 'right' }}>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCandidates.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '100px 0' }}>
                                            <Icon icon="solar:user-block-bold-duotone" width="64" style={{ color: 'var(--dr-primary)', opacity: 0.1, marginBottom: '16px' }} />
                                            <div style={{ fontWeight: 800, color: 'var(--dr-primary)', fontSize: '15px' }}>NO MATCHING RECORDS FOUND</div>
                                            <p style={{ color: '#cbd5e1', fontSize: '13px', marginTop: '4px' }}>Try adjusting your search or filters.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCandidates.map(r => (
                                        <tr key={r.record_id}>
                                            <td><span className="dr-chest-badge">{r.chest_number}</span></td>
                                            <td style={{ fontWeight: 700 }}>{r.full_name}</td>
                                            <td>{new Date(r.timestamp).toLocaleString()}</td>
                                            <td>
                                                <span style={{
                                                    padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 900,
                                                    background: r.result === 'PASS' ? '#dcfce7' : '#fee2e2',
                                                    color: r.result === 'PASS' ? '#166534' : '#991b1b'
                                                }}>
                                                    {r.result}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button onClick={() => setViewingResult(r)} style={{ border: 'none', background: 'var(--dr-primary)', color: 'white', padding: '6px 16px', borderRadius: '8px', fontSize: '11px', fontWeight: 800, cursor: 'pointer' }}>VIEW DETAIL</button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>

                {/* Side Drawer for Details */}
                <AnimatePresence>
                    {viewingResult && (
                        <>
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setViewingResult(null)} className="dr-drawer-overlay" />
                            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="dr-drawer">
                                <div className="dr-drawer-header">
                                    <h3 style={{ margin: 0, fontWeight: 900 }}>EVALUATION DETAILS</h3>
                                    <button onClick={() => setViewingResult(null)} className="dr-drawer-close"><Icon icon="ion:close" width="24" /></button>
                                </div>
                                <div className="dr-drawer-content hide-scrollbar">
                                    <div style={{ marginBottom: '32px', textAlign: 'center', padding: '24px', background: 'rgba(128,0,0,0.03)', borderRadius: '20px', border: '1px solid rgba(128,0,0,0.05)' }}>
                                        <div style={{ width: '64px', height: '64px', background: 'var(--dr-primary)', borderRadius: '16px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px', fontWeight: 900 }}>
                                            {viewingResult.chest_number}
                                        </div>
                                        <h2 style={{ fontSize: '22px', fontWeight: 900, margin: 0 }}>{viewingResult.full_name}</h2>
                                        <p style={{ color: 'var(--dr-text-muted)', fontSize: '12px', marginTop: '4px' }}>{viewingResult.unique_id}</p>
                                    </div>

                                    <div className="dr-drawer-section">
                                        <div className="dr-drawer-section-title">MEDICAL VITALS</div>
                                        <div className="dr-drawer-grid">
                                            {Object.entries(viewingResult.vitals).map(([key, value]) => (
                                                <div key={key} className="dr-drawer-stat">
                                                    <label>{key.toUpperCase().replace('_', ' ')}</label>
                                                    <span>{value || 'N/A'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="dr-drawer-section">
                                        <div className="dr-drawer-section-title">DOCTOR'S REMARKS</div>
                                        <p style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', fontSize: '13px', lineHeight: '1.6', border: '1px solid #e2e8f0' }}>
                                            {viewingResult.remarks || "No additional remarks provided."}
                                        </p>
                                    </div>

                                    <div className="dr-drawer-section">
                                        <div className="dr-drawer-section-title">AUTHORIZATION</div>
                                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', textAlign: 'center', background: 'white' }}>
                                            <img src={viewingResult.signature_hash} alt="Signature" style={{ maxHeight: '100px', filter: 'grayscale(100%) contrast(150%)' }} />
                                            <div style={{ fontSize: '11px', color: 'var(--dr-text-muted)', fontWeight: 700, marginTop: '12px' }}>
                                                Digitally Authorized at {new Date(viewingResult.timestamp).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        );
    };

    return (
        <SimulationProvider>
            <div className="dr-dashboard-container">
                {/* Sidebar (Exactly matched to Run Officer) */}
                <aside className={`dr-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="dr-sidebar-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
                        <Icon icon={isCollapsed ? "ion:chevron-forward" : "ion:chevron-back"} />
                    </div>

                    <div style={{
                        padding: isCollapsed ? '24px 0' : '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: isCollapsed ? '0' : '12px',
                        borderBottom: '1px solid var(--dr-border-light)',
                        minHeight: '89px',
                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <div style={{
                            width: '42px',
                            height: '42px',
                            background: 'var(--dr-primary)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            minWidth: '42px',
                            boxShadow: '0 4px 12px rgba(128, 0, 0, 0.2)',
                            margin: isCollapsed ? '0 auto' : '0'
                        }}>
                            <Icon icon="ion:shield-checkmark" width="22" />
                        </div>
                        {!isCollapsed && <span style={{ fontWeight: 800, fontSize: '18px', color: 'var(--dr-primary)', letterSpacing: '-0.5px' }}>ICFF INDIA</span>}
                    </div>

                    <nav
                        className="dr-nav-scrollbar"
                        style={{
                            padding: '20px 0',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            gap: '6px',
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden'
                        }}
                    >
                        {menuGroups.map((group, groupIdx) => (
                            <React.Fragment key={groupIdx}>
                                {!isCollapsed && (
                                    <div className="dr-sidebar-heading">
                                        {group.title}
                                    </div>
                                )}
                                {group.items.map(item => {
                                    const isActive = activeTab === item.id;
                                    return (
                                        <button
                                            key={item.id}
                                            className={`dr-nav-item ${isActive ? 'active' : ''}`}
                                            onClick={() => setActiveTab(item.id)}
                                            style={{
                                                position: 'relative',
                                                justifyContent: isCollapsed ? 'center' : 'flex-start',
                                                padding: isCollapsed ? '14px 0' : '14px 24px',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                border: 'none',
                                                background: 'none'
                                            }}
                                        >
                                            {isActive && (
                                                <motion.div
                                                    layoutId="drNavPill"
                                                    className="dr-nav-active-pill"
                                                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                                />
                                            )}
                                            {isActive && (
                                                <motion.div
                                                    layoutId="drNavIndicator"
                                                    className="dr-nav-active-indicator"
                                                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                                />
                                            )}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                position: 'relative',
                                                zIndex: 2,
                                                transform: isActive && !isCollapsed ? 'translateX(6px)' : 'none',
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}>
                                                <Icon
                                                    icon={item.icon}
                                                    width="24"
                                                    height="24"
                                                    style={{
                                                        color: isActive ? 'var(--dr-primary)' : 'var(--dr-sidebar-text)',
                                                        opacity: isActive ? 1 : 0.85,
                                                        filter: isActive ? 'drop-shadow(0 0 8px rgba(128, 0, 0, 0.3))' : 'none',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                />
                                                {!isCollapsed && <span style={{ fontSize: '15px', fontWeight: isActive ? 700 : 500 }}>{item.label}</span>}
                                            </div>
                                        </button>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </nav>

                    <div style={{ marginTop: 'auto', padding: '20px', borderTop: '1px solid var(--dr-border-light)' }}>
                        <button
                            className="universal-logout-btn"
                            onClick={onLogout}
                        >
                            <Icon icon="ion:log-out-outline" width="22" height="22" />
                            {!isCollapsed && <span style={{ fontWeight: 700 }}>Logout</span>}
                        </button>
                    </div>
                </aside>

                <div className="dr-content-area hide-scrollbar">
                    {/* Navbar (Exactly matched to Run Officer Layout) */}
                    <header className="dr-navbar">
                        <div className="dr-navbar-inner">
                            <div style={{ flex: 1 }}>
                                <div style={{ color: 'var(--dr-text-muted)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', display: 'flex', alignItems: 'center' }}>
                                    MEDICAL STATION <Icon icon="ion:chevron-forward-outline" width="12" style={{ margin: '0 8px' }} />
                                    <span style={{ color: 'var(--dr-primary)' }}>{activeTab?.toUpperCase()}</span>
                                </div>
                            </div>

                            {/* Center: Project Selection */}
                            <div style={{ flex: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
                                <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--dr-text-muted)', whiteSpace: 'nowrap' }}>SELECT PROJECT:</div>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <select
                                        value={selectedProject?._id || ''}
                                        onChange={(e) => setSelectedProject(assignedProjects.find(p => p._id === e.target.value))}
                                        style={{ background: 'white', color: 'var(--dr-primary)', padding: '10px 48px 10px 20px', borderRadius: '16px', fontSize: '13px', fontWeight: 800, border: '1px solid var(--dr-border-light)', cursor: 'pointer', appearance: 'none', minWidth: '350px', boxShadow: '0 2px 8px rgba(128, 0, 0, 0.05)' }}
                                    >
                                        {assignedProjects.length === 0 ? <option value="">No Active Projects</option> : assignedProjects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                    </select>
                                    <Icon icon="ion:chevron-down-outline" width="12" style={{ position: 'absolute', right: '16px', pointerEvents: 'none', color: 'var(--dr-primary)' }} />
                                </div>
                            </div>

                            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <div className="dr-profile-pill" onClick={() => setActiveTab('profile')}>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dr-text-main)' }}>DR. {user.name.toUpperCase()}</div>
                                        <div style={{ fontSize: '10px', color: 'var(--dr-primary)', fontWeight: 800, letterSpacing: '1px' }}>MEDICAL OFFICER</div>
                                    </div>
                                    <div className="dr-profile-avatar">
                                        <Icon icon="ion:person-circle-outline" width="26" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    <main className="dr-main-view">
                        {activeTab === 'evaluator' ? renderEvaluator() : activeTab === 'results' ? renderResults() : <Profile user={user} />}
                    </main>
                </div>

                {/* Simulation Remote (Floating) */}
                <DoctorDevTools projectId={selectedProject?._id} onActionSuccess={fetchQueue} />
            </div>
        </SimulationProvider>
    );
};

export default DoctorDashboard;
